import { professionalClient } from '@/httpClient/professionalClient'
import type {
  ICalendarEntryApi,
  IFullUserInfo,
  IGetMoneyTransferCustomerResponse
} from '@/models/interfaces'
import type {
  ObserverPaymentActivityDto,
  ObserverUpcomingScheduledPaymentDto
} from '@/pages/observer/services/observerPayments.types'
import type { SelectedClientUserId } from '@/types/observer.types'

type QueryValue = string | number | boolean | null | undefined

type QueryParams = Record<string, QueryValue>

const PROFESSIONAL_CLIENT_INFO_URL = '/api/professional/readOnly/GetClientInfo'
const PROFESSIONAL_OVERVIEW_URL = '/api/professional/overview'
const PROFESSIONAL_CONNECTIONS_URL = '/api/professional/connections'
const PROFESSIONAL_CONNECTION_DETAIL_URL =
  '/api/professional/connections/detail'
const PROFESSIONAL_CALENDAR_ENTRIES_URL =
  '/api/professional/Calendar/GetEntries'
const PROFESSIONAL_DELETED_CALENDAR_ENTRIES_URL =
  '/api/professional/Calendar/GetDeletedEntries'
const PROFESSIONAL_CALENDAR_ENTRY_URL = '/api/professional/Calendar/GetEntry'
const PROFESSIONAL_PAYMENT_CUSTOMER_URL =
  '/api/professional/MoneyTransfer/GetCustomer'
const PROFESSIONAL_PAYMENT_ACTIVITY_URL =
  '/api/professional/MoneyTransfer/GetActivityPaged'
const PROFESSIONAL_PAYMENT_DETAIL_URL =
  '/api/professional/MoneyTransfer/GetActivityById'
const PROFESSIONAL_PAYMENT_HISTORY_URL =
  '/api/professional/MoneyTransfer/GetPaymentHistoryById'
const PROFESSIONAL_SCHEDULED_PAYMENTS_URL =
  '/api/professional/MoneyTransfer/GetUpcomingScheduledPayments'

export interface ProfessionalPagedResponse<T> {
  items?: T[]
  totalCount?: number
  pageNumber?: number
  pageSize?: number
}

export interface ProfessionalOverviewResponse {
  [key: string]: unknown
}

export interface ProfessionalConnectionsResponse {
  [key: string]: unknown
}

export interface ProfessionalConnectionDetailResponse {
  [key: string]: unknown
}

export interface ProfessionalCalendarEntriesResponse {
  items?: ICalendarEntryApi[]
  events?: ICalendarEntryApi[]
}

/** GetEntry response envelope (backend GetEventResult). */
export interface ProfessionalCalendarEntryResponse {
  event: ICalendarEntryApi | null
}

/**
 * Professional endpoints wrap payloads in an ApiRequestResult envelope:
 * `{ value, errorCode, errorMessage }`. This unwraps `value` when the
 * envelope is present and passes raw payloads through (DEV mocks).
 */
function unwrapApiRequestResult<T>(data: unknown): T | null {
  if (data && typeof data === 'object' && 'value' in data) {
    return (data as { value: T | null }).value ?? null
  }
  return (data as T | null | undefined) ?? null
}

function compactParams(params: QueryParams): QueryParams {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  )
}

function withSelectedClientUserId(
  selectedClientUserId: SelectedClientUserId,
  params: QueryParams = {}
): QueryParams {
  return compactParams({ selectedClientUserId, ...params })
}

async function getOverview(): Promise<ProfessionalOverviewResponse> {
  const { data } = await professionalClient.get<ProfessionalOverviewResponse>(
    PROFESSIONAL_OVERVIEW_URL
  )
  return data
}

async function getConnections(
  params: QueryParams = {}
): Promise<ProfessionalConnectionsResponse> {
  const { data } =
    await professionalClient.get<ProfessionalConnectionsResponse>(
      PROFESSIONAL_CONNECTIONS_URL,
      { params: compactParams(params) }
    )
  return data
}

async function getConnectionDetail(
  params: QueryParams = {}
): Promise<ProfessionalConnectionDetailResponse> {
  const { data } =
    await professionalClient.get<ProfessionalConnectionDetailResponse>(
      PROFESSIONAL_CONNECTION_DETAIL_URL,
      { params: compactParams(params), skipRestrictedRedirect: true }
    )
  return data
}

async function getCalendarEntries(
  selectedClientUserId: SelectedClientUserId,
  startTimeInUtc: string,
  endTimeInUtc: string
): Promise<ProfessionalCalendarEntriesResponse> {
  const { data } =
    await professionalClient.get<ProfessionalCalendarEntriesResponse>(
      PROFESSIONAL_CALENDAR_ENTRIES_URL,
      {
        params: withSelectedClientUserId(selectedClientUserId, {
          startTimeInUtc,
          endTimeInUtc
        })
      }
    )
  return data
}

async function getDeletedCalendarEntries(
  selectedClientUserId: SelectedClientUserId,
  params: QueryParams = {}
): Promise<ProfessionalCalendarEntriesResponse> {
  const { data } =
    await professionalClient.get<ProfessionalCalendarEntriesResponse>(
      PROFESSIONAL_DELETED_CALENDAR_ENTRIES_URL,
      { params: withSelectedClientUserId(selectedClientUserId, params) }
    )
  return data
}

async function getCalendarEntry(
  selectedClientUserId: SelectedClientUserId,
  itemId: number,
  skipRestrictedRedirect?: boolean
): Promise<ProfessionalCalendarEntryResponse> {
  const { data } = await professionalClient.get<unknown>(
    PROFESSIONAL_CALENDAR_ENTRY_URL,
    {
      params: withSelectedClientUserId(selectedClientUserId, { itemId }),
      skipRestrictedRedirect
    }
  )
  return {
    event: (data as { event?: ICalendarEntryApi | null } | null)?.event ?? null
  }
}

async function getPaymentCustomer(
  selectedClientUserId: SelectedClientUserId,
  getLatestDocument: boolean
): Promise<IGetMoneyTransferCustomerResponse | null> {
  const { data } =
    await professionalClient.get<IGetMoneyTransferCustomerResponse | null>(
      PROFESSIONAL_PAYMENT_CUSTOMER_URL,
      {
        params: withSelectedClientUserId(selectedClientUserId, {
          getLatestDocument
        })
      }
    )
  return data
}

async function getPaymentActivity(
  selectedClientUserId: SelectedClientUserId,
  params: QueryParams = {}
): Promise<ObserverPaymentActivityDto[]> {
  const { data } = await professionalClient.get<ObserverPaymentActivityDto[]>(
    PROFESSIONAL_PAYMENT_ACTIVITY_URL,
    { params: withSelectedClientUserId(selectedClientUserId, params) }
  )
  return data
}

async function getPaymentDetail(
  selectedClientUserId: SelectedClientUserId,
  id: number,
  type: number
): Promise<ObserverPaymentActivityDto | null> {
  const { data } =
    await professionalClient.get<ObserverPaymentActivityDto | null>(
      PROFESSIONAL_PAYMENT_DETAIL_URL,
      { params: withSelectedClientUserId(selectedClientUserId, { id, type }) }
    )
  return data
}

async function getPaymentHistory(
  selectedClientUserId: SelectedClientUserId,
  scheduledPaymentId: number
): Promise<ObserverPaymentActivityDto[]> {
  const { data } = await professionalClient.get<ObserverPaymentActivityDto[]>(
    PROFESSIONAL_PAYMENT_HISTORY_URL,
    {
      params: withSelectedClientUserId(selectedClientUserId, {
        scheduledPaymentId
      })
    }
  )
  return data
}

async function getUpcomingScheduledPayments(
  selectedClientUserId: SelectedClientUserId
): Promise<ObserverUpcomingScheduledPaymentDto[]> {
  const { data } = await professionalClient.get<
    ObserverUpcomingScheduledPaymentDto[]
  >(PROFESSIONAL_SCHEDULED_PAYMENTS_URL, {
    params: withSelectedClientUserId(selectedClientUserId)
  })
  return data
}

/**
 * Fetch the observed parent's client info projection (identity, coparent,
 * timezone, date format) from the professional read-only endpoint
 * GET /api/professional/readOnly/GetClientInfo. The payload shape matches
 * IFullUserInfo field-for-field. skipRestrictedRedirect keeps a 404/403 on
 * this background lookup from bouncing the user to /restricted.
 */
async function getClientInfo(
  selectedClientUserId: SelectedClientUserId
): Promise<Partial<IFullUserInfo> | null> {
  const { data } = await professionalClient.get<Record<string, unknown>>(
    PROFESSIONAL_CLIENT_INFO_URL,
    {
      params: withSelectedClientUserId(selectedClientUserId),
      skipRestrictedRedirect: true
    }
  )
  return unwrapApiRequestResult<Partial<IFullUserInfo>>(data)
}

/**
 * Validates that the current professional has read access to the selected client.
 * Makes a lightweight call to the connections detail endpoint.
 * If the backend returns 403, the response interceptor handles navigation.
 * Returns true if access is confirmed, false if the call fails for non-auth reasons.
 */
async function validateClientAccess(
  selectedClientUserId: SelectedClientUserId
): Promise<boolean> {
  try {
    // Use the overview endpoint as a lightweight access check -
    // any professional endpoint with selectedClientUserId will trigger
    // the policy shell validation server-side.
    await professionalClient.get(PROFESSIONAL_CONNECTIONS_URL, {
      params: withSelectedClientUserId(selectedClientUserId)
    })
    return true
  } catch {
    // If we get here, the interceptor already handled 401/403 navigation.
    // Return false for caller awareness but don't double-navigate.
    return false
  }
}

export const observerApiService = {
  validateClientAccess,
  getClientInfo,
  getOverview,
  getConnections,
  getConnectionDetail,
  getCalendarEntries,
  getDeletedCalendarEntries,
  getCalendarEntry,
  getPaymentCustomer,
  getPaymentActivity,
  getPaymentDetail,
  getPaymentHistory,
  getUpcomingScheduledPayments
}
